// src/components/frameitforward/ExtrasModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, MusicDropIcon, ArrowLeft } from '../../public/icons';
import { Teddy, Time } from '../../public/images/shop';
import reviewService from '../../services/reviewService';
import AudioRecorder from '../general/AudioRecorder';

const STEP_DRAFT_KEY = 'kapta_frameitforward_step_extras';

const loadStepDraft = () => {
  try {
    const raw = localStorage.getItem(STEP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load extras draft:', err);
    return null;
  }
};

const saveStepDraft = (data) => {
  try {
    localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save extras draft:', err);
  }
};

const ExtrasModal = ({ isOpen, onClose, onBack, onContinue, onProgressChange }) => {
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedAudioItem, setSelectedAudioItem] = useState('');
  const [audioContent, setAudioContent] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  const [audioFile, setAudioFile] = useState(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [audioUploadError, setAudioUploadError] = useState('');

  const [showRecorder, setShowRecorder] = useState(false);

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on open ───────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadStepDraft();
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (Array.isArray(draft.selectedExtras))
      setSelectedExtras(draft.selectedExtras);
    if (typeof draft.selectedAudioItem === 'string')
      setSelectedAudioItem(draft.selectedAudioItem);
    if (typeof draft.audioContent === 'string')
      setAudioContent(draft.audioContent);
    if (typeof draft.selectedColor === 'string')
      setSelectedColor(draft.selectedColor);
    if (draft.audioFile !== undefined) setAudioFile(draft.audioFile);

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ───────── AUTOSAVE + broadcast on every change ───────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = {
      selectedExtras,
      selectedAudioItem,
      audioContent,
      selectedColor,
      audioFile
    };

    saveStepDraft(draft);
    onProgressChange?.(draft);
  }, [
    isOpen,
    selectedExtras,
    selectedAudioItem,
    audioContent,
    selectedColor,
    audioFile,
    onProgressChange
  ]);

  if (!isOpen) return null;

  const extrasOptions = [
    {
      id: 'greeting-card',
      name: 'Greeting Card',
      price: 5000,
      description: 'Add a handwritten-style card with your message.'
    },
    {
      id: 'wine',
      name: 'Bottle of Wine',
      price: 8000,
      description: 'Celebrate with something special.'
    },
    {
      id: 'audio-gift',
      name: 'Audio gift',
      price: 20000,
      description: 'Include an audio device embedded in a gift item.'
    }
  ];

  const handleExtraToggle = (extraId) => {
    if (selectedExtras.includes(extraId)) {
      setSelectedExtras((prev) => prev.filter((id) => id !== extraId));
      if (extraId === 'audio-gift') {
        setSelectedAudioItem('');
        setSelectedColor('');
        setAudioContent('');
        setAudioFile(null);
        setAudioUploadError('');
        setShowRecorder(false);
      }
    } else {
      setSelectedExtras((prev) => [...prev, extraId]);
    }
  };

  /* ───────── Upload audio to Cloudinary ───────── */
  const handleAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAudioUploadError('');

    if (!file.type.startsWith('audio/')) {
      setAudioUploadError('Please select an audio file (MP3, WAV, etc.).');
      return;
    }
    if (file.size > 14 * 1024 * 1024) {
      setAudioUploadError('Audio must be under 14MB.');
      return;
    }

    setIsUploadingAudio(true);
    try {
      const result = await reviewService.uploadAudioToCloudinary(file);
      setAudioFile({
        url: result.url,
        publicId: result.publicId,
        name: file.name,
        duration: result.duration
      });
    } catch (err) {
      console.error('Audio upload failed:', err);
      setAudioUploadError('Failed to upload audio. Please try again.');
    } finally {
      setIsUploadingAudio(false);
      event.target.value = '';
    }
  };

  const handleRemoveAudio = () => {
    setAudioFile(null);
    setAudioUploadError('');
  };

  const handleRecordingSaved = (recording) => {
    setAudioFile({
      url: recording.url,
      publicId: recording.publicId,
      name: recording.name,
      duration: recording.duration || null
    });
    setShowRecorder(false);
    setAudioUploadError('');
  };

  const handleContinue = () => {
    const extrasData = {
      selectedExtras,
      selectedAudioItem: selectedExtras.includes('audio-gift')
        ? selectedAudioItem
        : null,
      selectedColor: selectedExtras.includes('audio-gift')
        ? selectedColor
        : null,
      audioContent: selectedExtras.includes('audio-gift') ? audioContent : '',
      audioFile:
        selectedExtras.includes('audio-gift') && audioFile ? audioFile : null
    };
    onContinue(extrasData);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-4 mb-4 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Customise Your Giftbox
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Make this Frame it Forward box personal to the one you love.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4">
            <h3 className="text-lg font-medium text-[#1C2A46] text-left">
              Add Extras to Your Gift (Optional)
            </h3>
            <p className="text-[#6B635D] mb-6 text-left">
              You can include thoughtful items inside your gift box.
            </p>

            <div className="space-y-4">
              {extrasOptions.map((extra) => (
                <div
                  key={extra.id}
                  className="flex items-center justify-between w-full text-left transition-colors cursor-pointer"
                  onClick={() => handleExtraToggle(extra.id)}
                >
                  <div className="flex items-start gap-3 mb-5">
                    <div
                      className={`w-6 h-6 border-2 flex items-center justify-center mt-1 rounded ${
                        selectedExtras.includes(extra.id)
                          ? 'bg-[#1C2A46] border-[#1C2A46]'
                          : 'border-[#6B635D]'
                      }`}
                    >
                      {selectedExtras.includes(extra.id) && (
                        <svg
                          width="14"
                          height="10"
                          viewBox="0 0 14 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5L5 9L13 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-normal text-[#1C2A46] flex items-center justify-between w-full">
                        {extra.name}
                        <span className="text-green-600 font-normal ml-3">
                          +₦{extra.price.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-[#6B635D] font-light">
                        {extra.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedExtras.includes('audio-gift') && (
              <div className="mb-8 px-2 mt-6">
                <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                  Choose Audio Gift Item
                </h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setSelectedColor('wine');
                      setSelectedAudioItem('teddy-bear');
                    }}
                    className={`flex-1 pt-1 px-1 border text-center transition-colors ${
                      selectedColor === 'wine'
                        ? 'border-[#55000C]'
                        : 'border-[#E4E4E4] hover:border-[#55000C]'
                    }`}
                  >
                    <img src={Teddy} alt="Teddy Bear" />
                    <div className="flex justify-between items-center mt-3">
                      <div className="text-base font-normal text-[#1C2A46] mb-1">
                        Teddy Bear
                      </div>
                      <div className="text-[#389B51] font-normal text-sm">
                        +₦20,000
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedColor('midnight-blue');
                      setSelectedAudioItem('wooden-clock');
                    }}
                    className={`flex-1 pt-1 px-1 border text-center transition-colors ${
                      selectedColor === 'midnight-blue'
                        ? 'border-[#55000C]'
                        : 'border-[#E4E4E4] hover:border-[#55000C]'
                    }`}
                  >
                    <img src={Time} alt="Wooden Alarm clock" />
                    <div className="flex justify-between items-center mt-3">
                      <div className="text-base font-normal text-[#1C2A46] mb-1">
                        Wooden Alarm clock
                      </div>
                      <div className="text-[#389B51] font-normal text-sm">
                        +₦20,000
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {selectedExtras.includes('audio-gift') && (
              <div className="space-y-8 text-left mt-2">
                <div>
                  <h4 className="text-lg font-medium text-[#1C2A46]">
                    Add Audio Content
                  </h4>
                  <p className="text-[#6B635D] font-light text-sm mb-3">
                    Upload or record audio files that will play from the
                    selected gift item.
                  </p>

                  <div className="mb-4">
                    <label
                      htmlFor="audio-upload"
                      className={`block border-2 border-dashed p-8 text-center transition-colors ${
                        isUploadingAudio
                          ? 'border-[#D2A146] cursor-wait'
                          : 'border-[#E4E4E4] cursor-pointer hover:border-[#D2A146]'
                      }`}
                    >
                      <input
                        id="audio-upload"
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        onChange={handleAudioUpload}
                        disabled={isUploadingAudio}
                      />

                      {isUploadingAudio ? (
                        <>
                          <div className="w-12 h-12 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D2A146]" />
                          </div>
                          <div className="font-medium text-[#1C2A46] mb-1">
                            Uploading…
                          </div>
                        </>
                      ) : audioFile ? (
                        <>
                          <div className="w-12 h-12 bg-[#E7F4ED] rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#2F8F5B"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                          <div className="font-medium text-[#1C2A46] mb-1 truncate max-w-[280px] mx-auto">
                            {audioFile.name}
                          </div>
                          <div className="text-sm text-[#6B635D] mb-3">
                            Audio uploaded successfully
                          </div>
                          <div className="flex items-center justify-center gap-4">
                            <audio
                              src={audioFile.url}
                              controls
                              className="max-w-[260px]"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveAudio();
                            }}
                            className="mt-3 text-xs text-red-600 hover:underline"
                          >
                            Remove audio
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-3">
                            <MusicDropIcon />
                          </div>
                          <div className="font-medium text-[#1C2A46] mb-1">
                            Upload audio message
                          </div>
                          <div className="text-sm text-[#6B635D]">
                            MP3, WAV, M4A (max file size: 14mb)
                          </div>
                        </>
                      )}
                    </label>

                    {audioUploadError && (
                      <p className="mt-2 text-xs text-red-600 text-left">
                        {audioUploadError}
                      </p>
                    )}
                  </div>

                  {!showRecorder && !audioFile && (
                    <div className="mb-4 text-left">
                      <button
                        type="button"
                        onClick={() => setShowRecorder(true)}
                        className="text-sm text-[#55000C] underline hover:text-[#6a0a17] transition-colors"
                      >
                        Record Audio Instead
                      </button>
                    </div>
                  )}

                  {showRecorder && !audioFile && (
                    <div className="mb-6">
                      <AudioRecorder
                        onRecorded={handleRecordingSaved}
                        onCancel={() => setShowRecorder(false)}
                      />
                    </div>
                  )}

                  <div>
                    <div className="mb-6">
                      <label className="block text-base font-normal text-[#1C2A46] mb-2">
                        Audio Url
                      </label>
                      <input
                        type="text"
                        placeholder="www.spotify/music/superpowers-by-Adele"
                        value={audioContent}
                        onChange={(e) => setAudioContent(e.target.value)}
                        className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light placeholder:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 flex gap-4">
            <button
              onClick={onBack}
              className="flex w-1/4 justify-center items-center py-3 font-medium border border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146] transition-colors"
            >
              <ArrowLeft />
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={isUploadingAudio}
              className={`flex-1 py-3 font-medium transition-colors ${
                isUploadingAudio
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
              }`}
            >
              {isUploadingAudio ? 'Uploading…' : 'Save & Continue'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExtrasModal;